import { Injectable, inject } from '@angular/core';
import { ConstantsService } from '@catch/services';
import { JobPosting, Organization, Place } from 'schema-dts';
import { environment } from '../../environments/environment';
import { EmploymentType } from '../enums/employment-type.enum';
import { WorkingModel } from '../enums/working-model.enum';
import { CompanyInfo } from '../interfaces/company-info';
import { JobDetail } from '../interfaces/job-detail';
import countries from 'i18n-iso-countries';

@Injectable({
  providedIn: 'root',
})
export class GoogleJobPostingService {
  private readonly constantsService = inject(ConstantsService);

  setStructuredData(job: JobDetail, company: CompanyInfo) {
    const hiringOrganization: Organization = {
      '@type': 'Organization',
      name: company.title,
      sameAs: company.website,
      logo: `${environment.apiBaseUrl}/general/companies/${job.companyID}/logo`,
    };
    const jobLocation: Place[] = job.workLocations.map(
      (workLocation): Place => ({
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: workLocation.street,
          addressLocality: workLocation.city,
          postalCode: workLocation.postalCode,
          addressCountry: this.constantsService.getLanguageByCode(workLocation.countryCode)
          ?.name ?? 'de',
        },
      })
    );
    const jobPosting: JobPosting = {
      '@type': 'JobPosting',
      datePosted: job.postingTime,
      title: job.jobTitle,
      description: job.description
        .reduce(
          (a, b, i) =>
            `${a}${
              job.description[i] !== '' && i !== job.description.length
                ? '<br /><br />'
                : ''
            }${b}`
        )
        .replace(/<img .*?>/g, '')
        .replace(/<[^/>][^>]*><\/[^>]+>/g, ''),
      hiringOrganization,
      jobLocation,
      jobLocationType:
        job.workingModel !== WorkingModel.ON_SITE ? 'TELECOMMUTE' : null,
      applicantLocationRequirements: {
        '@type': 'Country',
        name: 'DE',
      },
      employmentType: this.mapEmploymentType(job.employmentType),
      directApply: true,
    };
    const structuredData = {
      '@context': 'http://schema.org/',
      ...jobPosting,
    };

    const id = 'googlejobs-data';
    document.getElementById(id)?.remove();
    const script = document.createElement('script');
    script.id = id;
    script.setAttribute('type', 'application/ld+json');
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }

  private mapEmploymentType(
    employmentType: EmploymentType | null
  ): GoogleJobEmploymentType {
    if (!employmentType) {
      return GoogleJobEmploymentType.OTHER;
    }
    switch (employmentType) {
      case EmploymentType.PERMANENT:
      case EmploymentType.LIMITED:
      case EmploymentType.TEMPORARY: {
        return GoogleJobEmploymentType.FULL_TIME;
      }
      case EmploymentType.BY_ARRANGEMENT: {
        return GoogleJobEmploymentType.PART_TIME;
      }
      case EmploymentType.INTERNSHIP:
      case EmploymentType.DUAL_STUDY:
      case EmploymentType.WORKING_STUDENT:
      case EmploymentType.TRAINEE:
      case EmploymentType.THESIS: {
        return GoogleJobEmploymentType.INTERN;
      }
      case EmploymentType.VOLUNTEERING: {
        return GoogleJobEmploymentType.VOLUNTEER;
      }
      case EmploymentType.FREELANCER:
      case EmploymentType.FEE_WORK: {
        return GoogleJobEmploymentType.CONTRACTOR;
      }
      default: {
        return GoogleJobEmploymentType.OTHER;
      }
    }
  }
}

enum GoogleJobEmploymentType {
  FULL_TIME = 'FULL_TIME',
  PART_TIME = 'PART_TIME',
  CONTRACTOR = 'CONTRACTOR',
  TEMPORARY = 'TEMPORARY',
  INTERN = 'INTERN',
  VOLUNTEER = 'VOLUNTEER',
  PER_DIEM = 'PER_DIEM',
  OTHER = 'OTHER',
}
